import React, {FormEvent, KeyboardEvent, useEffect, useRef, useState} from 'react';
import {alpha, AppBar, Button, InputBase, Toolbar as MatToolbar, Tooltip, Typography,} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {Search as SearchIcon} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {UserMenu} from '../UserMenu/UserMenu';
import {useDeactivateExtendedSearch, useDeactivateSearch} from '../../../utils/routes';
import {useLocation, useNavigate} from 'react-router-dom';
import {tooltipSimpleSearch} from './TooltipSimpleSearch';
import {useQueryParams} from '../AdminDataGrid/AdminDataGridHooks';

const useStyles = makeStyles((theme) => ({
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.background.paper, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.25),
    },
    marginLeft: -12,
    flexGrow: 1,
  },
  searchButton: {
    fontWeight: 'bold',
    marginLeft: 10,
  },
  clearIcon: {
    padding: theme.spacing(0, 2),
    height: 35,
    width: 35,
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'currentColor',
    transition: theme.transitions.create('opacity'),
    '&[hidden]': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
  iconButton: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.paper,
    margin: 1,
  },
  inputRoot: {
    color: 'inherit',
    width: '100%',
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputInput: {
    padding: theme.spacing(1),
    width: '100%',
  },
}));

export function Toolbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathUrl = location.pathname.split('/')[1];
  const queryParams = useQueryParams(location).q?.join(' ');

  const classes = useStyles();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(
    typeof queryParams === 'string' && queryParams.length ? queryParams : ''
  );
  const input = useRef<HTMLInputElement>(null);



  const locationEntity = location.pathname.split('/')[1];
  const tooltipSearch = tooltipSimpleSearch[locationEntity];
  const deactivateSearch = useDeactivateSearch(location.pathname);
  const deactivateExtendedSearch = useDeactivateExtendedSearch(location.pathname);

  useEffect(() => {
    if (typeof queryParams === 'string' && queryParams.length) {
      setSearchTerm(queryParams);
    } else {
      setSearchTerm('');
    }
  }, [queryParams]);

  function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    navigateSearch();
  }
  function navigateSearch() {
    const q = searchTerm ? `?q=${encodeURIComponent(searchTerm)}` : '';
    navigate(`/${pathUrl}${q}`);
  }
  function navSearch(){
    navigate(`/${pathUrl}/search${location.search}`);
  }
  function clearSearch() {
    setSearchTerm('');
  }
  function clearIfEsc(evt: KeyboardEvent<HTMLInputElement>) {
    if (evt.key === 'Escape') clearSearch();
  }

  const SuffixInput = React.forwardRef((props: any,ref: any)=>{
    const classes = useStyles();
    return(
    <div
      ref={ref}
      {...props}
      onClick={() => props.input.current?.focus()}
      className={classes.searchIcon}
      hidden={props.state.focused || (searchTerm?.length??0) > 0}
    >
      <SearchIcon color="secondary" />
      Søk
    </div>
    );
  });

  return (
    <AppBar position="sticky">
      <MatToolbar>
        <form className={classes.search} onSubmit={handleSubmit}>
          <InputBase
            inputRef={input}
            onKeyDown={clearIfEsc}
            hidden={deactivateSearch}
            value={searchTerm}
            onChange={(evt) => setSearchTerm(evt.target.value)}
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            inputProps={{ 'aria-label': t('Søk') }}
            renderSuffix={(state) => {
              if(!(tooltipSearch?.length))
                return(<SuffixInput  input={input} state={state} />);
              return(
              <Tooltip title={'Søker i ' + tooltipSearch} open={!(tooltipSearch?.length) ? false: undefined}>
                  <SuffixInput input={input} state={state} />
              </Tooltip>
            );}}
          />
        </form>
        <Button
          color="secondary"
          variant="outlined"
          aria-label={'Tøm'}
          hidden={deactivateSearch}
          className={classes.searchButton}
          onClick={clearSearch}
        >
          <Typography
            variant="subtitle1"
            component="h2"
          >
            {'Tøm'}
          </Typography>
        </Button>
        <Button
          color="secondary"
          variant="outlined"
          aria-label={'Utvidet'}
          hidden={deactivateSearch}
          disabled={ deactivateExtendedSearch}
          className={classes.searchButton}
          onClick={navSearch}
        >
          <Typography
            variant="subtitle1"
            component="h2"
          >
            {'Utvidet'}
          </Typography>
        </Button>
        <UserMenu />
      </MatToolbar>
    </AppBar>
  );
}
